import { useState } from "react";
import { useParams, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ArrowLeft, Send } from "lucide-react";

export default function AppFeedbackAcknowledgement() {
  const params = useParams<{ ticketId: string }>();
  const ticketId = parseInt(params.ticketId ?? "", 10);

  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpText, setFollowUpText] = useState("");
  const [done, setDone] = useState<"resolved" | "followup" | null>(null);

  const { data, isLoading, error } = trpc.feedbackAck.get.useQuery(
    { ticketId },
    { enabled: !isNaN(ticketId), retry: false }
  );

  const acknowledgeMutation = trpc.feedbackAck.acknowledge.useMutation({
    onSuccess: (_, vars) => {
      if (vars.resolved) setDone("resolved");
      else setShowFollowUp(true);
    },
  });

  const followUpMutation = trpc.feedbackAck.submitFollowUp.useMutation({
    onSuccess: () => setDone("followup"),
  });

  if (isNaN(ticketId)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <p className="text-stone-500">Ungültige Ticket-ID.</p>
        <Link href="/app" className="mt-4 text-sm text-[#8B6914] underline">Zurück zur App</Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-[#8B6914]" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <p className="text-stone-500">Dieses Feedback konnte nicht gefunden werden.</p>
        <Link href="/app" className="mt-4 text-sm text-[#8B6914] underline">Zurück zur App</Link>
      </div>
    );
  }

  // Already acknowledged
  if (done === "resolved" || (data.acknowledged && !data.reopened)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center gap-4">
        <CheckCircle2 className="h-12 w-12 text-green-500" />
        <h2 className="text-xl font-semibold text-stone-800">Danke für deine Bestätigung!</h2>
        <p className="text-stone-500 max-w-xs">Wir freuen uns, dass wir helfen konnten.</p>
        <Link href="/app">
          <Button variant="ghost" className="mt-2 text-[#8B6914]">
            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zur App
          </Button>
        </Link>
      </div>
    );
  }

  // Follow-up already sent
  if (done === "followup" || (data.reopened && data.followUpMessage)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center gap-4">
        <CheckCircle2 className="h-12 w-12 text-[#8B6914]" />
        <h2 className="text-xl font-semibold text-stone-800">Dein Follow-up wurde weitergeleitet. 🙏</h2>
        <p className="text-stone-500 max-w-xs">Unser Team meldet sich bald bei dir.</p>
        <Link href="/app">
          <Button variant="ghost" className="mt-2 text-[#8B6914]">
            <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zur App
          </Button>
        </Link>
      </div>
    );
  }

  const isSubmitting = acknowledgeMutation.isPending;

  return (
    <div className="max-w-lg mx-auto px-4 py-8">
      <Link href="/app">
        <button className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-6">
          <ArrowLeft className="h-4 w-4" /> Zurück
        </button>
      </Link>

      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
        <h1 className="text-lg font-semibold text-stone-800 mb-2">Wurde dein Problem gelöst?</h1>

        {data.resolution && (
          <div className="bg-stone-50 rounded-xl p-4 mb-6 text-sm text-stone-700 border border-stone-100">
            <p className="font-medium text-stone-500 mb-1 text-xs uppercase tracking-wide">Rückmeldung unseres Teams</p>
            <p>{data.resolution}</p>
          </div>
        )}

        {!showFollowUp ? (
          <div className="flex gap-3">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              disabled={isSubmitting}
              onClick={() => acknowledgeMutation.mutate({ ticketId, resolved: true })}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Ja, Problem gelöst
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              disabled={isSubmitting}
              onClick={() => acknowledgeMutation.mutate({ ticketId, resolved: false })}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Nein, noch nicht
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-stone-700">Was funktioniert noch nicht?</label>
            <textarea
              className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#8B6914]/30 resize-none min-h-[100px]"
              placeholder="Beschreibe kurz, was noch nicht funktioniert..."
              value={followUpText}
              onChange={(e) => setFollowUpText(e.target.value)}
            />
            <Button
              className="self-end bg-[#8B6914] hover:bg-[#7a5c10] text-white"
              disabled={!followUpText.trim() || followUpMutation.isPending}
              onClick={() => followUpMutation.mutate({ ticketId, message: followUpText.trim() })}
            >
              {followUpMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Senden
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
